import { useState } from "react";
import { fmtRub } from "../../lib/format";
import type { IssuerSlice } from "./dashboardData";

const SIZE = 180;
const RADIUS = 70;
const STROKE = 20;
const STROKE_HOVER = 24;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

// Интерактивный донат без отдельной легенды: при наведении сегмент
// расширяется, остальные тускнеют, в центре всплывает имя бумаги, сумма
// и доля портфеля. В нейтральном состоянии — счётчик уникальных бумаг.
export function IssuersDonut({ slices }: { slices: IssuerSlice[] }) {
  const [hi, setHi] = useState<number | null>(null);
  const total = slices.reduce((s, x) => s + x.rub, 0);
  const active = hi != null ? slices[hi] ?? null : null;

  let off = 0;
  const segs = slices.map((s, i) => {
    const len = total > 0 ? (s.rub / total) * CIRCUMFERENCE : 0;
    const dash = `${len} ${CIRCUMFERENCE - len}`;
    const dimmed = hi != null && hi !== i;
    const node = (
      <circle
        key={i}
        cx={SIZE / 2}
        cy={SIZE / 2}
        r={RADIUS}
        fill="none"
        stroke={s.color}
        strokeWidth={hi === i ? STROKE_HOVER : STROKE}
        strokeDasharray={dash}
        strokeDashoffset={-off}
        transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
        style={{
          cursor: "pointer",
          transition: "stroke-width 0.15s, opacity 0.15s",
          opacity: dimmed ? 0.35 : 1,
        }}
        onMouseEnter={() => setHi(i)}
        onMouseLeave={() => setHi(null)}
      />
    );
    off += len;
    return node;
  });

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="serif" style={{ fontSize: 20, marginBottom: 10 }}>
        По эмитентам
      </div>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div style={{ position: "relative", width: 220, height: 220 }}>
          <svg
            viewBox={`0 0 ${SIZE} ${SIZE}`}
            style={{ width: "100%", height: "100%" }}
          >
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              stroke="var(--paper-2)"
              strokeWidth={STROKE}
            />
            {segs}
          </svg>
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              textAlign: "center",
              padding: "0 18px",
            }}
          >
            {active ? (
              <>
                <div
                  className="soft"
                  title={active.name}
                  style={{
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 170,
                  }}
                >
                  {active.name}
                </div>
                <div
                  className="mono"
                  style={{ fontSize: 20, lineHeight: 1.1, marginTop: 4 }}
                >
                  {fmtRub(active.rub)}
                </div>
                <div
                  className="soft"
                  style={{ fontSize: 11.5, marginTop: 2 }}
                >
                  {((active.rub / total) * 100).toFixed(1)} % портфеля
                  {active.positions > 1 && ` · ×${active.positions}`}
                </div>
              </>
            ) : (
              <>
                <div className="mono" style={{ fontSize: 30, lineHeight: 1 }}>
                  {slices.length}
                </div>
                <div
                  className="soft"
                  style={{
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    marginTop: 2,
                  }}
                >
                  {slices.length === 1 ? "бумага" : "бумаг"}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div
        className="soft"
        style={{
          fontSize: 11.5,
          textAlign: "center",
          marginTop: 6,
          fontStyle: "italic",
        }}
      >
        наведи на сегмент — детали
      </div>
    </div>
  );
}
